/**
 * useCheckoutState — all useState declarations for the checkout flow.
 * Extracted from useCheckout so the main hook stays under the line limit.
 */
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CURRENCIES } from "@/utils/localization";
import { getAvailableDates as getAvailableDatesUtil, getTimeSlots as getTimeSlotsUtil } from "@/utils/scheduleUtils";

function fmtPrice(amount, currency = "EUR") {
  if (typeof amount !== "number" || isNaN(amount)) amount = 0;
  const config = CURRENCIES[currency] || CURRENCIES.EUR;
  const formatted = amount.toFixed(2).replace(".", ",");
  return config.position === "before" ? `${config.symbol}${formatted}` : `${formatted}${config.symbol}`;
}

export function useCheckoutState({ restaurant, restaurantId, API, paidOrderId, isPaid, cancelledOrderId, waPayReturn, waPayCancel }) {
  const [_searchParams] = useSearchParams();
  const pendingReviewOrderId = _searchParams.get("order_pending_review");
  const linkedCoupon = (_searchParams.get("coupon") || "")
    .trim().toUpperCase().replace(/[^A-Z0-9_-]/g, "").slice(0, 40);
  const fmt = (amount) => fmtPrice(amount, restaurant?.display_currency || restaurant?.currency || "EUR");
  const sc = restaurant?.scheduling_config || {};
  const getAvailableDates = () => getAvailableDatesUtil(restaurant, sc, new Date(), orderType);
  const getTimeSlots = (dateStr) => getTimeSlotsUtil(restaurant, sc, dateStr, new Date(), orderType);

  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState(1);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [orderConfirmed, setOrderConfirmed] = useState(null);
  const [confirmedCart, setConfirmedCart] = useState([]);
  const [showAllergenInfo, setShowAllergenInfo] = useState(false);

  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryLat, setDeliveryLat] = useState(null);
  const [deliveryLng, setDeliveryLng] = useState(null);
  const [addressNumber, setAddressNumber] = useState("");
  const [addressComplement, setAddressComplement] = useState("");
  const [addressBarrio, setAddressBarrio] = useState("");

  const [scheduleDate, setScheduleDate] = useState("asap");
  const [scheduleTime, setScheduleTime] = useState("");

  const [couponCode, setCouponCode] = useState(linkedCoupon);
  const [referralDiscount, setReferralDiscount] = useState(null);
  const [loyaltyReward, setLoyaltyReward] = useState(null); // { id, reward, points_required, discount_type, discount_value, discount_amount }
  const [paymentMethod, setPaymentMethod] = useState("online");
  const [orderType, setOrderType] = useState(linkedCoupon ? "pickup" : "delivery");
  const [orderNotes, setOrderNotes] = useState("");
  const [changeFor, setChangeFor] = useState("");

  const [showReservation, setShowReservation] = useState(false);
  const [reservationData, setReservationData] = useState({
    customer_name: "", customer_phone: "", customer_email: "",
    date: "", time: "20:00", guests: 2, seating_preference: "sin_preferencia", notes: "",
  });
  const [reservationSubmitting, setReservationSubmitting] = useState(false);
  const [reservationConfirmed, setReservationConfirmed] = useState(false);
  const [reservationSlotAlternatives, setReservationSlotAlternatives] = useState([]);

  const fullAddress = deliveryAddress
    ? `${deliveryAddress} ${addressNumber}${addressComplement ? `, ${addressComplement}` : ""}${addressBarrio ? `, ${addressBarrio}` : ""}`
    : "";

  const saveCustomerLocal = (phone, name) => {
    localStorage.setItem(`kaiso_customer_${restaurantId}`, JSON.stringify({ phone, name }));
  };

  useEffect(() => {
    if (paidOrderId && isPaid) {
      axios.post(`${API}/public/order/${paidOrderId}/confirm-payment`)
        .then(res => setOrderConfirmed({ ...res.data, order_id: res.data.order_id || paidOrderId }))
        .catch(() => {
          toast.warning("A confirmar pagamento — aguarde ou atualize a página");
          setOrderConfirmed({ id: paidOrderId, order_id: paidOrderId, payment_status: "pending" });
        });
    }
    // Recovery for pending_verification: Viva redirected before transaction was queryable.
    // confirm-payment re-verifies directly with Viva API and activates the order if paid.
    if (pendingReviewOrderId && !paidOrderId) {
      axios.post(`${API}/public/order/${pendingReviewOrderId}/confirm-payment`)
        .then(res => {
          if (res.data?.payment_status === "paid") {
            setOrderConfirmed({ ...res.data, order_id: res.data.order_id || pendingReviewOrderId });
          } else {
            toast.info("Pagamento a ser verificado. Receberá confirmação em breve.");
          }
        })
        .catch(() => toast.warning("A verificar pagamento — aguarde ou atualize a página."));
    }
    if (cancelledOrderId) {
      axios.patch(`${API}/public/order/${cancelledOrderId}/cancel`).catch(() => {});
    }
    if (cancelledOrderId || waPayCancel) {
      toast.error("El pago fue cancelado. Tu pedido no ha sido procesado.");
    }
  }, [paidOrderId, isPaid, pendingReviewOrderId, cancelledOrderId, waPayCancel, API]);

  useEffect(() => {
    if (!waPayReturn || !API) return undefined;
    let attempts = 0;
    const maxAttempts = 30;
    const poll = setInterval(async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${API}/public/wa-payment-status/${waPayReturn}`);
        const data = res.data || {};
        if (data.status === "paid" && data.order_id) {
          clearInterval(poll);
          setOrderConfirmed({
            id: data.order_id,
            order_id: data.order_id,
            payment_status: "paid",
          });
          toast.success("¡Pago confirmado! Puedes volver a WhatsApp.");
          return;
        }
        if (data.status === "processing" && attempts >= maxAttempts) {
          clearInterval(poll);
          toast.info("Pago en proceso. Te avisaremos por WhatsApp.");
        }
      } catch {
        if (attempts >= maxAttempts) clearInterval(poll);
      }
      if (attempts >= maxAttempts) clearInterval(poll);
    }, 2000);
    return () => clearInterval(poll);
  }, [waPayReturn, API]);

  return {
    fmt, getAvailableDates, getTimeSlots, saveCustomerLocal, fullAddress,
    showCheckout, setShowCheckout, checkoutStep, setCheckoutStep,
    customerName, setCustomerName, customerPhone, setCustomerPhone,
    submitting, setSubmitting, orderConfirmed, setOrderConfirmed, confirmedCart, setConfirmedCart,
    showAllergenInfo, setShowAllergenInfo,
    deliveryAddress, setDeliveryAddress, deliveryLat, setDeliveryLat, deliveryLng, setDeliveryLng,
    addressNumber, setAddressNumber, addressComplement, setAddressComplement, addressBarrio, setAddressBarrio,
    scheduleDate, setScheduleDate, scheduleTime, setScheduleTime,
    couponCode, setCouponCode, referralDiscount, setReferralDiscount, loyaltyReward, setLoyaltyReward,
    paymentMethod, setPaymentMethod, orderType, setOrderType, orderNotes, setOrderNotes, changeFor, setChangeFor,
    showReservation, setShowReservation, reservationData, setReservationData,
    reservationSubmitting, setReservationSubmitting, reservationConfirmed, setReservationConfirmed,
    reservationSlotAlternatives, setReservationSlotAlternatives,
  };
}
